import { Attachment as AttachmentDto } from "@/lib/types";
import axios from "axios";
import {
  Attachment,
  createVideoPlaceholder,
  CreateVideoPlaceholderPayload,
} from "./attachment";
import { getActiveRegion } from "@/config/regions";
import { queryClient } from "@/lib/queryClient";
import { ACCEPTED_VIDEO_MIME_TYPES } from "@/lib/media/video";
import { editRecipeSchema } from "@/components/images/image-studio/edit-recipe.schema";
import type { RetouchRequest } from "@/components/images/image-studio/retouch-request";

const CONV_URL = import.meta.env.VITE_CONVERTISSEUR_URL;

/** Accepted video mime types + extensions (mirrors the converter whitelist). */
export const ACCEPTED_VIDEO_TYPES = [...ACCEPTED_VIDEO_MIME_TYPES] as const;

/** Max uploaded video size in MB. Beyond this, use a YouTube/Vimeo link. */
export const VIDEO_MAX_SIZE_MB = 100;

function resolveVideoUploadName(preferredName: string | undefined, file: File): string {
  const trimmedPreferred = preferredName?.trim();
  if (trimmedPreferred) {
    return trimmedPreferred;
  }

  const fromFileName = file.name
    .replace(/\.[^/.]+$/, "")
    .trim();
  if (fromFileName) {
    return fromFileName;
  }

  return `video-${Date.now().toString(36)}`;
}

export async function uploadImage(
  file: File,
  data: AttachmentDto,
  s3Bucket: string,
  instanceId: string,
  url?: string,
  displayName?: string,
): Promise<Attachment> {
  const region = getActiveRegion();
  const apiUrl = region.baseUrl;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", data.name);
  formData.append("position", data.position);
  formData.append("legend", data.legend || "");
  formData.append("legendEN", data.legendEN || "");
  formData.append("details", data.details || "");
  formData.append("docId", data.docId);
  formData.append("order", data.order?.toString() || "-1");
  formData.append("published", data.published ? "true" : "false");
  formData.append(
    "applyWatermark",
    data.applyWatermark !== false ? "true" : "false",
  );
  formData.append("descriptionFR", data.descriptionFR || "");
  formData.append("descriptionEN", data.descriptionEN || "");
  formData.append("textAltFR", data.textAltFR || data.name || "");
  formData.append("textAltEN", data.textAltEN || "");
  formData.append("S3_BUCKET", s3Bucket);
  formData.append("instanceId", instanceId);
  formData.append("isHd", data.isHd ? "true" : "false");
  formData.append("provider", region.storageProvider);
  formData.append("region", region.code);

  if (data.caption) formData.append("caption", JSON.stringify(data.caption));
  if (data.textAlt) formData.append("textAlt", JSON.stringify(data.textAlt));
  if (data.description)
    formData.append("description", JSON.stringify(data.description));
  if (data.notes) formData.append("notes", JSON.stringify(data.notes));
  formData.append("photoCredits", data.photoCredits ?? "");
  if (displayName) formData.append("displayName", displayName);

  if (!url) {
    formData.append("API_URL", apiUrl + "back/api/attachment/create/unique");
  } else {
    formData.append("API_URL", apiUrl + url);
  }

  const response = await axios.post(CONV_URL + "uploadV3", formData);
  return response.data.image;
}

/**
 * Uploads a video: first creates a "processing" placeholder attachment via the
 * API, then sends the file to the converter which transcodes it to MP4 H.264
 * asynchronously and calls back the API once ready. The caller should poll the
 * attachment (via its docId) until `videoStatus === "ready"`.
 *
 * @returns the processing placeholder attachment.
 */
export async function uploadVideo(
  file: File,
  data: CreateVideoPlaceholderPayload,
  s3Bucket: string,
): Promise<Attachment> {
  if (file.size > VIDEO_MAX_SIZE_MB * 1024 * 1024) {
    throw new Error("videoTooLarge");
  }

  const region = getActiveRegion();
  const apiUrl = region.baseUrl;

  // 1) Create the processing placeholder (authenticated).
  const placeholder = await createVideoPlaceholder(data);

  // 2) Send the file to the converter for async transcoding.
  const uploadName = resolveVideoUploadName(data.name, file);
  const formData = new FormData();
  formData.append("file", file);
  formData.append("attachmentId", placeholder.id);
  formData.append("name", uploadName);
  formData.append("S3_BUCKET", s3Bucket);
  formData.append("API_URL", apiUrl + "back/api/attachment");
  formData.append("provider", region.storageProvider);
  formData.append("region", region.code);
  if (data.instanceId) formData.append("instanceId", data.instanceId);

  await axios.post(CONV_URL + "uploadV3/video", formData);

  await queryClient.invalidateQueries({ queryKey: ["attachment"] });

  return placeholder;
}

export interface FileUpload {
  sourceName: string;
  title: string;
  tag: string;
  docId: string;
  isHd?: boolean;
  mimetype: string;
  description?: string;
  instanceId?: string;
}

export async function uploadDocument(
  file: File,
  s3bucket: string,
  data: FileUpload,
) {
  const region = getActiveRegion();
  const apiUrl = region.baseUrl;

  const formData = new FormData();
  formData.append("doc", file);
  formData.append("S3_BUCKET", s3bucket);
  formData.append("sourceName", data.sourceName);
  formData.append("title", data.title);
  formData.append("tag", data.tag);
  formData.append("description", data.description ?? "");
  formData.append("docId", data.docId);
  formData.append("mimetype", data.mimetype);
  formData.append("API_URL", apiUrl + "back/api/file/create");
  formData.append("provider", region.storageProvider);
  formData.append("region", region.code);
  if (data.instanceId) {
    formData.append("instanceId", data.instanceId);
  }

  try {
    await axios.post(CONV_URL + "uploadV3/document", formData);
  } catch (e) {
    console.error(e);
  }
}

export type RetouchImageParams = RetouchRequest & {
  s3Bucket: string;
  instanceId?: string;
};

export type RetouchImageResult = {
  status: string;
  weight?: number;
};

/**
 * Demande au converter d'appliquer une `EditRecipe` et de régénérer les
 * dérivés sur place (cf. IMAGE_EDITOR.md). L'original figé n'est jamais
 * écrasé. Après succès, invalide le cache des attachments.
 *
 * Route réelle : `POST {VITE_CONVERTISSEUR_URL}uploadV3/retouch`
 * (le contrat parle de `/v3/retouch` ; l'upload V3 est déjà monté ainsi).
 */
export async function retouchImage(
  params: RetouchImageParams,
): Promise<RetouchImageResult> {
  const parsed = editRecipeSchema.safeParse(params.recipe);
  if (!parsed.success) {
    throw new Error("invalidEditRecipe");
  }
  if (!params.attachmentId || !params.link || !params.s3Bucket) {
    throw new Error("missingRetouchTarget");
  }

  const region = getActiveRegion();
  const apiUrl = region.baseUrl;

  const { data } = await axios.post<RetouchImageResult>(
    CONV_URL + "uploadV3/retouch",
    {
      attachmentId: params.attachmentId,
      link: params.link,
      isHd: params.isHd,
      isFirstEdit: params.isFirstEdit,
      S3_BUCKET: params.s3Bucket,
      provider: region.storageProvider,
      region: region.code,
      recipe: parsed.data,
      applyWatermark: params.applyWatermark !== false,
      API_URL: apiUrl + "back/api/attachment",
      ...(params.instanceId ? { instanceId: params.instanceId } : {}),
    },
  );

  await queryClient.invalidateQueries({ queryKey: ["attachment"] });

  return data;
}
